/**
 * App.tsx
 * ──────────────────────────────────────────────────────────────────────────
 * The root component, shared by Android, iOS and web.
 *
 * PROVIDER ORDER IS LOAD-BEARING:
 *   SafeAreaProvider  device insets — the header must clear a notch
 *   ErrorBoundary     one place a render crash is caught, above everything
 *                     that could crash
 *   SessionProvider   who is signed in; installs the 401 handler
 *   ConfigProvider    the workspace; waits for a session before fetching
 *   ShellProvider     view / drawer / modal state; reads the console from
 *                     ConfigProvider, so it must sit inside it
 *
 * ToastHost is mounted LAST and outside the providers' visual tree so it always
 * paints above everything, including modals. On web it portals itself above
 * react-native-web's modal layer — see components/Toast/ToastHost.web.tsx.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StatusBar, StyleSheet, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { ToastHost } from './src/components';
import ErrorBoundary from './src/components/ErrorBoundary';
import { neutral } from './src/theme/colors';
import { SessionProvider } from './src/context/SessionContext';
import { ConfigProvider } from './src/context/ConfigContext';
import { ShellProvider } from './src/context/ShellContext';
import RootNavigator from './src/navigation/RootNavigator';

export default function App() {
  return (
    <SafeAreaProvider>
      <View style={styles.root}>
        {/* Dark glyphs on the white chrome — this design has no dark mode. */}
        <StatusBar barStyle="dark-content" backgroundColor={neutral.surface} />
        <ErrorBoundary>
          <SessionProvider>
            <ConfigProvider>
              <ShellProvider>
                <RootNavigator />
              </ShellProvider>
            </ConfigProvider>
          </SessionProvider>
        </ErrorBoundary>
        <ToastHost />
      </View>
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: neutral.surface },
});
